import { cn } from "@/lib/utils";
import { type RefObject, useEffect, useState } from "react";
import { HintCursorIcon } from "./hint-cursor-icon";

const CELL_DOM_PREFIX = "dsd-editor-cell-";
const GAP = 6;
const PAD = 3;
// 진입/퇴장 애니메이션 시간
const ANIM_MS = 50;

/**
 * 문서 진입 시 대상 첫 셀에 파란 강조 보더 + 안내 칩 + 손 커서를 띄우는 원타임 코치마크.
 * 사용자 입력(클릭·키·휠·터치)이 발생하면 사라진다. (프로그램 스크롤은 무시)
 */
export const ReviewCellHint = ({
  scrollContainerRef,
  onDismiss,
  isTargetCell,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
  onDismiss: () => void;
  /** 강조할 셀의 판정. 문서 순서상 이 판정을 만족하는 첫 셀에 붙는다 */
  isTargetCell: (cellId: string) => boolean;
}) => {
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  // 퇴장 애니메이션 → 끝나면 언마운트
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(onDismiss, ANIM_MS);
    return () => clearTimeout(timer);
  }, [leaving, onDismiss]);

  // 대상 셀(문서 순서상 첫 셀)을 찾아 화면 안으로 스크롤 + 위치 계산.
  // layout effect 가 아니라 effect — 같은 커밋에 마운트되는 문서 뷰가 layout effect 로 스크롤을 맨 위로 되돌리는데,
  // 자식인 이쪽이 먼저 돌면 그 초기화가 이 스크롤을 지워 고정 위치 박스가 셀에서 어긋난다
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cells = container.querySelectorAll<HTMLElement>(
      `[id^='${CELL_DOM_PREFIX}']`,
    );
    let target: HTMLElement | null = null;
    for (const el of cells) {
      if (isTargetCell(el.dataset.cellId ?? "")) {
        target = el;
        break;
      }
    }
    if (!target) {
      setRect(null);
      return;
    }

    // 화면 밖이면 셀을 가운데로 스크롤(칩이 위에 들어갈 여유 확보). inline:center = 가로 경계(그라데이션/더보기) 회피
    target.scrollIntoView({ block: "center", inline: "center" });

    const compute = () => {
      const r = target.getBoundingClientRect();
      setRect({
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
      });
    };
    compute();
    // 박스는 fixed 라 프로그램 스크롤(닫지 않는다)에도 셀을 따라가야 한다. 부드러운 스크롤은 프레임당 여러 번 오므로 프레임당 한 번만 잰다
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", schedule);
    container.addEventListener("scroll", schedule, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      container.removeEventListener("scroll", schedule);
    };
  }, [scrollContainerRef, isTargetCell]);

  // 사용자 입력(클릭·키·휠·터치)이면 부드럽게 퇴장. 프로그램 스크롤엔 안 닫힘.
  // 닫는 클릭의 잔여 이벤트를 피해 다음 틱부터 감지.
  useEffect(() => {
    const dismiss = () => setLeaving(true);
    const capture = { capture: true } as const;
    const capturePassive = { capture: true, passive: true } as const;
    const timer = setTimeout(() => {
      window.addEventListener("pointerdown", dismiss, capture);
      window.addEventListener("keydown", dismiss, capture);
      window.addEventListener("wheel", dismiss, capturePassive);
      window.addEventListener("touchmove", dismiss, capturePassive);
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", dismiss, capture);
      window.removeEventListener("keydown", dismiss, capture);
      window.removeEventListener("wheel", dismiss, capture);
      window.removeEventListener("touchmove", dismiss, capture);
    };
  }, []);

  if (!rect) return null;

  return (
    <>
      {/* 파란 강조 보더 */}
      <div
        aria-hidden
        data-testid="review-cell-hint-box"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
        className={cn(
          "pointer-events-none fixed z-[60] rounded-[6px] border-2 border-primary-color ring-4 ring-primary-color/24 duration-[50ms] ease-out fill-mode-forwards",
          leaving
            ? "animate-out fade-out-0 zoom-out-95"
            : "animate-in fade-in-0 zoom-in-95",
        )}
      />
      {/* 안내 칩 (셀 위, 셀 중앙 정렬) — 반지름 2px, 여백 4/2, 파란 칩 */}
      <div
        style={{ top: rect.top - GAP, left: rect.left + rect.width / 2 }}
        className="pointer-events-none fixed z-[60] -translate-x-1/2 -translate-y-full"
      >
        <span
          className={cn(
            "inline-flex whitespace-nowrap rounded-[2px] bg-primary-color px-1 py-0.5 caption-1-medium text-static-white duration-[50ms] ease-out fill-mode-forwards",
            leaving
              ? "animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-1"
              : "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-1",
          )}
        >
          셀을 클릭하면 검토 결과를 확인할 수 있어요
        </span>
      </div>
      {/* 손 커서 — 검지 끝이 셀 오른쪽 아래 모서리 안쪽에 오도록 */}
      <HintCursorIcon
        className={cn(
          "pointer-events-none fixed z-[60] duration-[50ms] ease-out fill-mode-forwards",
          leaving
            ? "animate-out fade-out-0 zoom-out-95"
            : "animate-in fade-in-0 zoom-in-95",
        )}
        style={{
          top: rect.top + rect.height - 14,
          left: rect.left + rect.width - 20,
        }}
      />
    </>
  );
};
